import { useState } from 'react';
import type { Measure } from '@ehr/shared';
import { MeasureHistory } from './NoteMeasures.js';
import { Modal } from './Modal.js';
import { Dd } from './Dropdown.js';
import { DateField } from './DateTimeField.js';
import { RailFoldBtn, type RailFold } from './RailFold.js';
import { filterPast, pastKind, pastMeta, pastSections, type PastNote } from '../lib/noteHistory.js';

/**
 * ehr#437 part 6 (PROGRESS_NOTE_10-14-V81 Last session, History panel, copy forward): the note
 * rail's Last session card and the History panel, where a past note's parts copy into today's
 * boxes (below what's there), its Interventions used ticks copy, or all of it at once.
 */
export interface CopyApi {
  /** Today's text boxes a past part can go into. */
  targets: { key: string; label: string }[];
  /** Interventions used is on today's note. */
  interventionsShown: boolean;
  locked: boolean;
  /** Copies text into a box (from = the past note's date); says what happened. */
  copyText: (text: string, key: string, from: string) => string;
  copyTicks: (items: string[]) => string;
  copyAll: (n: PastNote) => string;
}

const ticksOf = (n: PastNote) =>
  Array.isArray(n.body.interventionsUsed)
    ? n.body.interventionsUsed.filter((x): x is string => typeof x === 'string')
    : [];

/** One past note's parts, each with its copy action. */
export function PastNoteView({
  note,
  api,
  onDone,
}: {
  note: PastNote;
  api: CopyApi;
  onDone: (msg: string) => void;
}) {
  const [into, setInto] = useState<Record<string, string>>({});
  const ticks = ticksOf(note);
  return (
    <div className="pn-past">
      {pastSections(note).map((s) => {
        const same = api.targets.find((t) => t.key === s.key);
        return (
          <div key={s.key} className="pn-past-sec">
            <div className="pn-past-sh">
              <b>{s.label}</b>
              {!api.locked &&
                (same ? (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => onDone(api.copyText(s.text, same.key, note.date))}
                  >
                    Copy to {same.label}
                  </button>
                ) : (
                  api.targets.length > 0 && (
                    <span className="pn-inline-field pn-past-into">
                      <Dd
                        value={into[s.key] ?? ''}
                        searchable={false}
                        placeholder="Copy into"
                        ariaLabel={`Copy ${s.label} into`}
                        floating
                        options={api.targets.map((t) => ({ value: t.key, label: t.label }))}
                        onChange={(k) => {
                          if (!k) return;
                          setInto((m) => ({ ...m, [s.key]: '' }));
                          onDone(api.copyText(s.text, k, note.date));
                        }}
                      />
                    </span>
                  )
                ))}
            </div>
            <p className="pn-past-t">{s.text}</p>
          </div>
        );
      })}
      {ticks.length > 0 && (
        <div className="pn-past-sec">
          <div className="pn-past-sh">
            <b>Interventions used</b>
            {!api.locked && api.interventionsShown && (
              <button type="button" className="btn sm" onClick={() => onDone(api.copyTicks(ticks))}>
                Copy ticks
              </button>
            )}
          </div>
          <p className="pn-past-t">{ticks.join(', ')}</p>
        </div>
      )}
    </div>
  );
}

/** The rail's Last session card: when, the plan and the assessment (or the layout's first boxes). */
export function LastSessionCard({
  note,
  api,
  fmtDate,
  author,
  onPast,
  fold,
  kind,
}: {
  /** ehr#571 (case management round, V20): null on an Other note with no signed note of its kind. */
  note: PastNote | null;
  /** The open note's kind, for "No signed <kind> note yet." */
  kind?: string;
  api: CopyApi;
  fmtDate: (iso: string) => string;
  author: (id: string) => string;
  onPast: () => void;
  /** ehr#437 reopen (V81 rail): the card folds to its header. */
  fold?: RailFold;
}) {
  const [full, setFull] = useState(false);
  const [msg, setMsg] = useState('');
  const secs = note ? pastSections(note) : [];
  const shown = ['plan', 'assessment', 'subjective', 'data', 'behavior']
    .map((k) => secs.find((s) => s.key === k))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .slice(0, 2);
  if (fold && !fold.open)
    return (
      <section className="pn-card pn-last pn-rail-shut" aria-label="Last session">
        <div className="pn-rail-h">
          <h3>Last session</h3>
          <span className="pn-sp" />
          <RailFoldBtn fold={fold} title="Last session" />
        </div>
      </section>
    );
  if (!note)
    return (
      <section className="pn-card pn-last" aria-label="Last session">
        <div className="pn-rail-h">
          <h3>Last session</h3>
          <span className="pn-sp" />
          <button type="button" className="pn-link" onClick={onPast}>
            Past sessions
          </button>
          {fold && <RailFoldBtn fold={fold} title="Last session" />}
        </div>
        <p className="pn-past-t pn-last-none" data-test="ls-empty">
          No signed {kind ?? ''} note yet.
        </p>
      </section>
    );
  return (
    <section className="pn-card pn-last" aria-label="Last session">
      <div className="pn-rail-h">
        <h3>Last session</h3>
        <span className="pn-sp" />
        <button type="button" className="pn-link" onClick={onPast}>
          Past sessions
        </button>
        <button type="button" className="pn-link" onClick={() => setFull(true)}>
          Full note
        </button>
        {fold && <RailFoldBtn fold={fold} title="Last session" />}
      </div>
      <div className="pn-last-meta">{pastMeta(note, fmtDate)}</div>
      {shown.map((s) => (
        <div key={s.key}>
          <div className="pn-last-k">{s.label}</div>
          <p className="pn-past-t">{s.text}</p>
        </div>
      ))}
      {msg && (
        <p className="pn-rail-msg" role="status">
          {msg}
        </p>
      )}
      {full && (
        <Modal
          title={`Last session, ${fmtDate(note.date)}`}
          onClose={() => setFull(false)}
          className="pn-past-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setFull(false)}>
                Close
              </button>
              {!api.locked && (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    setFull(false);
                    setMsg(api.copyAll(note));
                  }}
                >
                  Copy all into matching boxes
                </button>
              )}
            </>
          }
        >
          <div className="pn-last-meta">
            {pastMeta(note, fmtDate)} · {author(note.authorId)}
          </div>
          <PastNoteView
            note={note}
            api={api}
            onDone={(m) => {
              setFull(false);
              setMsg(m);
            }}
          />
        </Modal>
      )}
    </section>
  );
}

/** The History panel (in the rail): past sessions with search, kind and dates; open one to copy. */
export function HistoryPanel({
  notes,
  api,
  fmtDate,
  author,
  onClose,
  measures = [],
  initialTab = 'notes',
  measureKey,
  measureBox = '',
  onAddLine,
}: {
  notes: PastNote[];
  api: CopyApi;
  fmtDate: (iso: string) => string;
  author: (id: string) => string;
  onClose: () => void;
  /** ehr#437 (V81 measure history): the client's scored measures, for the Measures tab. */
  measures?: Measure[];
  initialTab?: 'notes' | 'measures';
  measureKey?: string;
  /** The box a measure's sentence goes into ("Objective"). */
  measureBox?: string;
  onAddLine?: (line: string) => string;
}) {
  const [tab, setTab] = useState<'notes' | 'measures'>(measures.length ? initialTab : 'notes');
  const [f, setF] = useState({ q: '', kind: '', from: '', to: '' });
  const [shown, setShown] = useState(5);
  const [openId, setOpenId] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const kinds = [...new Set(notes.map(pastKind))];
  const all = filterPast(notes, f, author);
  const filtered = Boolean(f.q.trim() || f.kind || f.from || f.to);
  const open = openId ? notes.find((n) => n.id === openId) : null;
  return (
    <section className="pn-card pn-hist" aria-label="History">
      <div className="pn-rail-h">
        <h3>History</h3>
        <span className="pn-sp" />
        <button type="button" className="pn-x" aria-label="Close history" onClick={onClose}>
          ×
        </button>
      </div>
      {measures.length > 0 && (
        <div className="tc-tabs pn-hist-tabs" role="tablist" aria-label="History">
          {(
            [
              ['notes', 'Past sessions'],
              ['measures', 'Measures'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={`tc-tab${tab === k ? ' on' : ''}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {tab === 'measures' ? (
        <MeasureHistory
          measures={measures}
          initialKey={measureKey}
          boxLabel={measureBox}
          locked={api.locked}
          onAdd={onAddLine ?? (() => '')}
        />
      ) : open ? (
        <>
          <div className="pn-hist-nh">
            <button type="button" className="pn-link" onClick={() => setOpenId(null)}>
              Back to the list
            </button>
            <span className="pn-sp" />
            {!api.locked && (
              <button
                type="button"
                className="btn sm primary"
                onClick={() => setMsg(api.copyAll(open))}
              >
                Copy all into matching boxes
              </button>
            )}
          </div>
          <div className="pn-last-meta">
            <b>{pastMeta(open, fmtDate)}</b> · {author(open.authorId)}
          </div>
          <PastNoteView note={open} api={api} onDone={setMsg} />
        </>
      ) : (
        <>
          <input
            className="pn-hist-q"
            value={f.q}
            placeholder="Search past notes"
            aria-label="Search past notes"
            onChange={(e) => {
              setF({ ...f, q: e.target.value });
              setShown(5);
            }}
          />
          <div className="pn-hist-fr">
            <label className="pn-hist-f">
              <span>Kind</span>
              <Dd
                value={f.kind}
                searchable={false}
                placeholder="All kinds"
                ariaLabel="Kind of note"
                options={[
                  { value: '', label: 'All kinds' },
                  ...kinds.map((k) => ({ value: k, label: k })),
                ]}
                onChange={(v) => {
                  setF({ ...f, kind: v });
                  setShown(5);
                }}
              />
            </label>
            <label className="pn-hist-f">
              <span>From</span>
              <DateField value={f.from} onChange={(v) => setF({ ...f, from: v })} />
            </label>
            <label className="pn-hist-f">
              <span>To</span>
              <DateField value={f.to} onChange={(v) => setF({ ...f, to: v })} />
            </label>
          </div>
          {filtered && (
            <p className="pn-hist-count">
              {all.length} of {notes.length} note{notes.length === 1 ? '' : 's'}
            </p>
          )}
          <div className="pn-hist-list">
            {all.slice(0, shown).map((n) => (
              <div key={n.id} className="pn-hist-row">
                <button type="button" className="pn-link" onClick={() => setOpenId(n.id)}>
                  {pastMeta(n, fmtDate)}
                </button>
                <span>{author(n.authorId)}</span>
              </div>
            ))}
            {all.length === 0 && (
              <p className="pn-hist-empty">
                {notes.length ? 'No past notes match.' : 'No signed notes for this client yet.'}
              </p>
            )}
          </div>
          {all.length > shown && (
            <button type="button" className="pn-link" onClick={() => setShown((x) => x + 5)}>
              Show more ({all.length - shown})
            </button>
          )}
        </>
      )}
      {msg && (
        <p className="pn-rail-msg" role="status">
          {msg}
        </p>
      )}
    </section>
  );
}
